import React, { useEffect, useRef, useState } from 'react';
import { ARCHETYPES } from '../../config/archetypes';

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function MockExamWizard({
  questions,
  currentIndex,
  answers,
  remainingSeconds,
  onAnswerChange,
  onNavigate,
  onSubmit,
  onCancel,
}) {
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [showFiveMinBanner, setShowFiveMinBanner] = useState(false);
  const hasWarnedFiveMin = useRef(false);
  const question = questions[currentIndex];
  const archetype = question?.archetype || 'outline_4';
  const archetypeConfig = ARCHETYPES[archetype] ?? ARCHETYPES.outline_4;
  const cards = archetypeConfig.cards;
  const marks = question?.marks ?? archetypeConfig.marks ?? 4;

  const currentSteps = answers[question?.id]?.steps ?? {};
  const isLast = currentIndex === questions.length - 1;
  const isFirst = currentIndex === 0;

  const isAlmostOut = remainingSeconds <= 300 && remainingSeconds > 0;
  const isUrgent = remainingSeconds <= 60;

  // One-shot 5-minute warning banner. Only triggers if we entered the wizard
  // with > 5 min on the clock (so resumed exams already past 5:00 don't flash).
  useEffect(() => {
    if (hasWarnedFiveMin.current) return;
    if (remainingSeconds > 300) return;
    if (remainingSeconds <= 0) return;
    hasWarnedFiveMin.current = true;
    setShowFiveMinBanner(true);
    const t = setTimeout(() => setShowFiveMinBanner(false), 6000);
    return () => clearTimeout(t);
  }, [remainingSeconds]);

  if (!question) return null;

  const handleNav = (newIndex) => {
    onNavigate(newIndex, Date.now());
  };

  const handleSubmitClick = () => {
    if (confirmingSubmit) {
      setConfirmingSubmit(false);
      onSubmit(Date.now());
    } else {
      setConfirmingSubmit(true);
    }
  };

  return (
    <div className="relative flex flex-col h-full">
      {/* Cancel confirmation popup */}
      {confirmingCancel && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 rounded-2xl">
          <div className="bg-[#161b22] border border-[#30363d] rounded-xl px-6 py-5 mx-6 shadow-2xl text-center space-y-4">
            <div className="text-[#e6edf3] text-sm font-semibold">Cancel exam?</div>
            <p className="text-[#8b949e] text-xs leading-relaxed">
              Your progress will be lost and this exam will not be saved.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setConfirmingCancel(false)}
                className="px-4 py-2 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] text-xs font-mono transition-colors"
              >
                Keep going
              </button>
              <button
                onClick={onCancel}
                className="px-4 py-2 rounded bg-[#f85149]/20 border border-[#f85149]/40 text-[#f85149] hover:bg-[#f85149]/30 text-xs font-mono transition-colors"
              >
                Yes, cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5-minute warning banner */}
      {showFiveMinBanner && (
        <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2 bg-[#d29922]/15 border-b border-[#d29922]/40">
          <span className="text-[#d29922] text-xs font-mono">⏱ 5 minutes remaining — wrap up your final answers.</span>
          <button
            onClick={() => setShowFiveMinBanner(false)}
            className="text-[#d29922] hover:text-[#e6edf3] text-xs font-mono"
            aria-label="Dismiss warning"
          >
            ✕
          </button>
        </div>
      )}

      {/* Sticky header */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-[#30363d] bg-[#0d1117]">
        <div className="text-xs font-mono text-[#8b949e]">
          Q{currentIndex + 1}/{questions.length}
        </div>
        <div
          className={`font-mono text-base font-bold tabular-nums transition-colors ${
            isUrgent ? 'text-[#f85149]' : isAlmostOut ? 'text-[#d29922]' : 'text-[#e6edf3]'
          }`}
        >
          {formatTime(remainingSeconds)}
        </div>
        <div className="flex items-center gap-3">
          {/* Progress dots — show up to 12, then show n/total */}
          {questions.length <= 12 ? (
            <div className="flex gap-1">
              {questions.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentIndex
                      ? 'bg-[#58a6ff]'
                      : i < currentIndex
                      ? 'bg-[#3fb950]'
                      : 'bg-[#30363d]'
                  }`}
                />
              ))}
            </div>
          ) : (
            <span className="text-[10px] font-mono text-[#8b949e]">
              {currentIndex + 1}/{questions.length}
            </span>
          )}
          <button
            onClick={() => setConfirmingCancel(true)}
            className="w-6 h-6 flex items-center justify-center rounded-full text-[#484f58] hover:text-[#e6edf3] hover:bg-[#30363d] transition-colors text-xs"
            aria-label="Cancel exam"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Question body */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider">
              {question.year}{question.paper != null ? ` · Paper ${question.paper}` : question.source_paper ? ` · ${question.source_paper}` : ''} · {question.question_number}
            </div>
            <span
              className="text-[9px] font-mono px-1.5 py-0.5 rounded border"
              style={{
                color: archetypeConfig.hex,
                borderColor: `${archetypeConfig.hex}40`,
                backgroundColor: `${archetypeConfig.hex}10`,
              }}
            >
              {archetypeConfig.label}
            </span>
          </div>
          <p className="text-[#e6edf3] text-sm leading-relaxed font-medium">
            {question.question_text}
          </p>
          <div className="text-[10px] font-mono text-[#8b949e] mt-1">[{marks} marks]</div>
        </div>

        {cards.map((card) => {
          const value = currentSteps[card.label] ?? '';
          return (
            <div key={card.label} className="space-y-1">
              <div className="flex items-baseline justify-between">
                <label className="text-[10px] font-mono font-semibold text-slate-400 uppercase tracking-wider">
                  {card.label}
                </label>
                <span className="text-[9px] font-mono text-[#484f58]">
                  {value.trim() ? value.trim().split(/\s+/).length : 0}/{card.maxWords}w
                </span>
              </div>
              <textarea
                className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-[#e6edf3] text-xs leading-relaxed resize-none focus:outline-none focus:border-slate-500/60 transition-colors placeholder-[#484f58]"
                rows={card.rows}
                placeholder={card.purpose}
                value={value}
                onChange={(e) =>
                  onAnswerChange(question.id, card.label, e.target.value)
                }
              />
            </div>
          );
        })}
      </div>

      {/* Navigation footer */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-t border-[#30363d] bg-[#0d1117] gap-2">
        <button
          onClick={() => handleNav(currentIndex - 1)}
          disabled={isFirst}
          className="px-4 py-2 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff]/40 text-xs font-mono transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ← Prev
        </button>

        {confirmingSubmit ? (
          <div className="flex items-center gap-2">
            <span className="text-[#d29922] text-xs font-mono">Submit all answers?</span>
            <button
              onClick={handleSubmitClick}
              className="px-3 py-1.5 rounded bg-[#f85149]/20 border border-[#f85149]/40 text-[#f85149] text-xs font-mono hover:bg-[#f85149]/30 transition-colors"
            >
              Confirm
            </button>
            <button
              onClick={() => setConfirmingSubmit(false)}
              className="px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] text-xs font-mono hover:text-[#e6edf3] transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={isLast ? handleSubmitClick : undefined}
            className={`px-4 py-2 rounded border text-xs font-mono transition-colors ${
              isLast
                ? 'border-[#f85149]/40 text-[#f85149] hover:bg-[#f85149]/10'
                : 'border-transparent text-transparent cursor-default'
            }`}
            style={{ visibility: isLast ? 'visible' : 'hidden' }}
          >
            Submit Exam
          </button>
        )}

        <button
          onClick={() => handleNav(currentIndex + 1)}
          disabled={isLast}
          className="px-4 py-2 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff]/40 text-xs font-mono transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
